import React, { memo, useMemo } from 'react';
import { TaskStatusEnum, TaskTypesEnum } from '@redux/tasks/tasksTypes';
import { NotepadIcon, WaveformIcon } from 'components/Icons';
import { TableHeader } from 'components/TableHeader/TableHeader';
import { Color } from 'utils/color';
import { TasksTableCell } from '../TasksTableCell/TasksTableCell';
import style from './TasksTable.scss';
import { TasksTableColName, TasksTableProps } from './TasksTable.types';

const TASK_TYPE_ICONS = {
  [TaskTypesEnum.MESSAGE]: WaveformIcon,
  [TaskTypesEnum.ASSESSMENT]: NotepadIcon,
  //TODO: MESSAGING is oudated type
  MESSAGING: WaveformIcon
};

export const TasksTable: React.FC<TasksTableProps> = memo(({ data, onSelectTaskSwitch, onCancelTask }) => {
  const rows = useMemo(() => {
    const result: JSX.Element[] = [];

    for (const { taskId, avatar, taskName, taskSummary, patientResponse, scheduledForDateTime, patientFullName, status, frequency, taskType } of data) {
      if (status === TaskStatusEnum.CANCELED) {
        continue;
      }

      const onSelect = () => {
        onSelectTaskSwitch(taskId);
      };

      const onCancel = (isSingleCancel: boolean) => {
        onCancelTask(taskId, isSingleCancel);
      };

      // Default to wave icon for now as requested: SW-3057
      const Icon = TASK_TYPE_ICONS[taskType.toUpperCase()] ?? WaveformIcon;
      const taskIcon = <Icon width={16} height={16} color={Color.DARK_BLUE} />;

      result.push(
        <tr key={taskId} onClick={onSelect} className={style.tableRow}>
          <TasksTableCell containerClassName={style.nameColumn} colName={TasksTableColName.PATIENT_NAME} value={patientFullName} avatarUrl={avatar} />
          <TasksTableCell containerClassName={style.dateColumn} colName={TasksTableColName.SCHEDULED_DATE} value={scheduledForDateTime} />
          <TasksTableCell containerClassName={style.mediumColumn} colName={TasksTableColName.FREQUENCY} value={frequency} />
          <TasksTableCell containerClassName={style.mediumColumn} colName={TasksTableColName.TASK_TYPE} value={taskType} icon={taskIcon} />
          <TasksTableCell containerClassName={style.taskSummaryColumn} colName={TasksTableColName.TASK_NAME} value={taskSummary || taskName} />
          <TasksTableCell containerClassName={style.patientMessageColumn} colName={TasksTableColName.PATIENT_RESPONSE} value={patientResponse} />
          <TasksTableCell containerClassName={style.statusColumn} colName={TasksTableColName.STATUS} value={status} />
          <TasksTableCell
            containerClassName={style.detailsColumn}
            colName={TasksTableColName.DETAILS}
            value={taskId}
            onSelectTask={onSelect}
            onCancelTask={onCancel}
            taskStatus={status}
            frequency={frequency}
          />
        </tr>
      );
    }

    return result;
  }, [data, onSelectTaskSwitch, onCancelTask]);

  return (
    <div className={style.tableWrapper}>
      <table className={style.patientsTable}>
        <thead>
          <tr className={style.tableRow}>
            <TableHeader containerClassName={style.nameColumn}>Patient</TableHeader>
            <TableHeader containerClassName={style.dateColumn}>Scheduled for</TableHeader>
            <TableHeader containerClassName={style.smallColumn}>Frequency</TableHeader>
            <TableHeader containerClassName={style.smallColumn}>Task type</TableHeader>
            <TableHeader containerClassName={style.taskSummaryColumn}>Task Summary</TableHeader>
            <TableHeader containerClassName={style.patientMessageColumn}>Patient response</TableHeader>
            <TableHeader containerClassName={style.statusColumn}>Status</TableHeader>
            <TableHeader containerClassName={style.detailsColumn}>{''}</TableHeader>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
});
